import { useRef, useState, type ChangeEvent, type TextareaHTMLAttributes } from 'react';

import { TextArea } from './Modal';

/**
 * A multi-line field with its OWN undo/redo, independent of the browser's
 * native per-field history.
 *
 * WHY NOT RELY ON Ctrl+Z: investigated against the voucher/invoice editors —
 * no value transformation, no remount-on-keystroke, no global key
 * interception, no autosave mid-typing. Nothing found that would break native
 * undo in THESE fields specifically, which means it most likely already
 * works. But "probably works, in this browser, today" is not something a
 * staff member filling out a document can rely on, and the explicit buttons
 * the product asked for are strictly better anyway: they are visible, they
 * work the same in every browser, and they do not depend on focus being in
 * the right element.
 *
 * COALESCED, NOT PER-KEYSTROKE. A history entry is pushed only after a pause
 * in typing (`COALESCE_MS`), the same granularity a real text editor groups
 * undo steps at — one Undo for "the sentence just typed", not one per
 * character.
 *
 * RESET ON `resetKey`. A parent that re-seeds its whole draft from a fresh
 * server object (switching to a different document, not a refetch of the
 * same one) must clear this field's history too, or the first keystroke
 * against the NEW value would be diffed against the OLD value still held in
 * this component's internal "last committed" ref — pass the same identity
 * the parent re-seeds on (e.g. `voucher.id`).
 */
export function UndoableTextArea({
  value,
  onChange,
  resetKey,
  ...rest
}: Omit<TextareaHTMLAttributes<HTMLTextAreaElement>, 'value' | 'onChange'> & {
  value: string;
  onChange: (e: ChangeEvent<HTMLTextAreaElement>) => void;
  /** Identity the parent re-seeds this field's value on. See doc comment. */
  resetKey?: string;
}): JSX.Element {
  const [past, setPast] = useState<string[]>([]);
  const [future, setFuture] = useState<string[]>([]);
  const lastCommitted = useRef(value);
  const lastResetKey = useRef(resetKey);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);

  if (resetKey !== lastResetKey.current) {
    lastResetKey.current = resetKey;
    lastCommitted.current = value;
    if (past.length > 0) setPast([]);
    if (future.length > 0) setFuture([]);
  }

  const COALESCE_MS = 600;

  function fire(next: string): void {
    onChange({ target: { value: next } } as unknown as ChangeEvent<HTMLTextAreaElement>);
  }

  function handleChange(e: ChangeEvent<HTMLTextAreaElement>): void {
    const next = e.target.value;
    onChange(e);
    if (timer.current !== null) clearTimeout(timer.current);
    timer.current = setTimeout(() => {
      if (next !== lastCommitted.current) {
        setPast((p) => [...p, lastCommitted.current]);
        setFuture([]);
        lastCommitted.current = next;
      }
    }, COALESCE_MS);
  }

  function undo(): void {
    if (past.length === 0) return;
    if (timer.current !== null) {
      clearTimeout(timer.current);
      timer.current = null;
    }
    const previous = past[past.length - 1] as string;
    setPast((p) => p.slice(0, -1));
    setFuture((f) => [value, ...f]);
    lastCommitted.current = previous;
    fire(previous);
  }

  function redo(): void {
    if (future.length === 0) return;
    const next = future[0] as string;
    setFuture((f) => f.slice(1));
    setPast((p) => [...p, value]);
    lastCommitted.current = next;
    fire(next);
  }

  return (
    <div>
      <TextArea value={value} onChange={handleChange} {...rest} />
      <div className="mt-1.5 flex gap-1.5">
        <button
          type="button"
          onClick={undo}
          disabled={past.length === 0}
          aria-label="Undo"
          title="Undo"
          className="rounded-[6px] border border-neutral-300 px-2 py-0.5 text-[11px] font-semibold leading-none text-neutral-600 hover:border-ink hover:text-ink disabled:cursor-not-allowed disabled:opacity-40"
        >
          Undo
        </button>
        <button
          type="button"
          onClick={redo}
          disabled={future.length === 0}
          aria-label="Redo"
          title="Redo"
          className="rounded-[6px] border border-neutral-300 px-2 py-0.5 text-[11px] font-semibold leading-none text-neutral-600 hover:border-ink hover:text-ink disabled:cursor-not-allowed disabled:opacity-40"
        >
          Redo
        </button>
      </div>
    </div>
  );
}
